import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTextDomainContext } from "@/app/providers";
import {
  useAddUserCompany,
  useCompanies,
  useCountries,
  useRolesList,
} from "@/hooks";
import {
  Autocomplete,
  Box,
  Button,
  Drawer,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { bool, func, object } from "prop-types";
import { isEqual } from "lodash";
import { useParams } from "react-router";
import type { CustomersType } from "@/types/graphql";

type AddUserCompanyProps = {
  open: boolean;
  handleClose: () => void;
  row: {
    id?: string | number | null;
  };
};

type CompanyRoleType = {
  id: string;
  role: string;
  key: string;
  modules: unknown[];
  createdAt: string;
  updatedAt: string;
  value: string;
  label: string;
};

export const AddUserCompany = (props: AddUserCompanyProps) => {
  //region Loading data
  const { uid: userId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const { countries, loading: countriesLoading } = useCountries();
  const { customers, loading: loadingCustomers } = useCompanies({});
  const { rolesList } = useRolesList();

  const initialValue = useMemo(() => {
    return {
      inputCountry: countries[0] || null,
      inputCompany: customers[0] || null,
      inputRole: rolesList[rolesList.length - 1] || null,
    };
  }, [countries, customers, rolesList]);
  //endregion Loading data

  //region React Hook (useState)
  const [inputCountry, setInputCountry] = useState(initialValue.inputCountry);
  const [inputCustomer, setInputCustomer] = useState(initialValue.inputCompany);
  const [inputRole, setInputRole] = useState<CompanyRoleType | null>(
    initialValue.inputRole,
  );
  //endregion React Hook (useState)

  const allCustomers = (customers as Array<CustomersType>).filter(
    (row: CustomersType) => row.country === inputCountry?.code,
  );
  const companyRoles = (rolesList as Array<CompanyRoleType>).filter((row) =>
    row.key.startsWith("company-"),
  );

  //region React Hook (useEffect)
  useEffect(() => {
    if (!inputCountry?.code) {
      if (countries.length) {
        if (!isEqual(inputCountry, countries[0])) {
          // Use setTimeout to defer state update and avoid cascading renders
          setTimeout(() => {
            setInputCountry(countries[0]);
          }, 0);
        }
      }
    }
  }, [countries, initialValue, inputCountry]);

  //endregion React Hook (useEffect)

  //region Handlers

  const resetForm = useCallback(() => {
    // setUserId(initialValue.userId);
    // setInputName(initialValue.inputName);
    // setInputEmail(initialValue.inputEmail);
    // setInputPassword(initialValue.inputPassword);
  }, []);

  const { addCustomer, loading: adding } = useAddUserCompany(() => {
    enqueueSnackbar(
      inputCustomer?.name + gettext(": The new company has been added"),
      {
        variant: "success",
      },
    );
    // setCustomerName("");
    // setinputRole("");
  });

  // const { addUserByAdmin, loading: adding } = useAddUserByAdmin(
  //   () => {
  //     resetForm();
  //     // handleClose();
  //     enqueueSnackbar(gettext("The new user has been added."), {
  //       variant: "success"
  //     });
  //   },
  //   error => {
  //     if (error === "409") {
  //       // setEmailError(true);
  //     } else if (error === "410") {
  //       enqueueSnackbar(gettext("You are not authorized to perform this action"), {
  //         variant: "error"
  //       });
  //     } else {
  //       enqueueSnackbar(gettext("Error: Something went wrong!"), {
  //         variant: "error"
  //       });
  //     }
  //   }
  // );

  // const { updateUser, loading: updating } = useUpdateUser(
  //   () => {
  //     resetForm();
  //     // handleClose();
  //     enqueueSnackbar(gettext("The user has been updated"), {
  //       variant: "success"
  //     });
  //   },
  //   error => {
  //     if (error === "409") {
  //       // setEmailError(true);
  //     } else if (error === "410") {
  //       enqueueSnackbar(gettext("You are not authorized to perform this action"), {
  //         variant: "error"
  //       });
  //     } else {
  //       enqueueSnackbar(`Backend Error: ${error}`, {
  //         variant: "error"
  //       });
  //     }
  //   }
  // );

  const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // addUserByAdmin({
    //   variables: {
    //     name: inputName,
    //     email: inputEmail,
    //     password: inputPassword,
    //     role: [inputRole?.key],
    //     partnerId: inputPartner?.id,
    //     countryCode: inputCountry?.code,
    //     lang: inputLang?.value,
    //     timezone: inputTimezone.zoneName,
    //     status: inputStatus.value
    //   },
    //   refetchQueries: ["comMyUsers"]
    // });

    const variables = {
      userId: userId,
      role: inputRole?.key,
      customerName: inputCustomer?.name,
    };

    addCustomer(variables);

    // addCustomer({
    //   variables: variables,
    //   refetchQueries: ["getUserCustomers"]
    // });
  };

  const handleClose = () => {
    resetForm();
    props.handleClose();
  };
  //endregion Handlers

  return (
    <Drawer
      anchor={"right"}
      open={props.open}
      onClose={handleClose}
      sx={{
        zIndex: (theme) => theme.zIndex.appBar + 1,
      }}
      slotProps={{
        paper: {
          sx: {
            padding: 4,
            minWidth: 400,
            maxWidth: 720,
          },
        },
      }}
    >
      <Box
        sx={{
          width: "auto",
        }}
      >
        <form onSubmit={submitHandler}>
          <Stack
            spacing={4}
            sx={{
              height: "100%",
            }}
          >
            <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
              {!props.row.id && gettext("Attach Company with User")}
              {props.row.id && gettext("Edit User Company")}
            </Typography>

            <Box
              sx={{
                height: "100%",
              }}
            >
              <Grid
                container
                spacing={4}
                sx={{
                  height: "100%",
                }}
              >
                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    disablePortal
                    options={countries}
                    getOptionLabel={(option) => option.name || ""}
                    getOptionKey={(option) => option.code || ""}
                    value={inputCountry}
                    onChange={(e, newValue) => {
                      setInputCountry(newValue);
                      // setInputTimezone(initialValue.inputTimezone);
                    }}
                    size="small"
                    fullWidth
                    isOptionEqualToValue={(option, value) =>
                      option?.code?.toLowerCase() === value?.code?.toLowerCase()
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label={gettext("Country")}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    disablePortal
                    options={companyRoles}
                    getOptionLabel={(option) => option.role || ""}
                    getOptionKey={(option) => option.key || ""}
                    value={inputRole}
                    onChange={(e, newValue) => setInputRole(newValue)}
                    size="small"
                    fullWidth
                    isOptionEqualToValue={(option, value) =>
                      option?.key?.toLowerCase() === value?.key?.toLowerCase()
                    }
                    renderInput={(params) => (
                      <TextField {...params} required label={gettext("Role")} />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    disablePortal
                    options={allCustomers}
                    getOptionLabel={(option) => option.name || ""}
                    getOptionKey={(option) => option.id || ""}
                    value={inputCustomer}
                    onChange={(e, newValue) => setInputCustomer(newValue)}
                    size="small"
                    fullWidth
                    isOptionEqualToValue={(option, value) =>
                      option?.id?.toLowerCase() === value?.id?.toLowerCase()
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label={gettext("Company")}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>

            <Paper elevation={0}>
              <Grid
                container
                spacing={4}
                sx={{
                  width: "100%",
                }}
              >
                <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleClose()}
                  >
                    {gettext("Close")}
                  </Button>
                </Grid>

                <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
                  <Button
                    variant="contained"
                    loading={adding || countriesLoading || loadingCustomers}
                    fullWidth
                    type="submit"
                  >
                    {gettext("Add")}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </form>
      </Box>
    </Drawer>
  );
};

AddUserCompany.propTypes = {
  open: bool,
  handleClose: func,
  row: object,
};
